import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import {
  calculateTaskStatsByNcount,
  getAssessmentStatisticsContext,
  getAssessmentTypes,
  getCountries,
  getCountryScreenersByCountryId,
  previewNormalizedScoreRecalculation,
  publishTaskStatistics,
  runNormalizedScoreRecalculation,
} from '../services/APIClient';

// This is a new, separate component from CalculateStandardisedScoresModal.js —
// deliberately not sharing that file, so the existing one-shot flow it drives
// stays completely untouched while this step-by-step flow is tested
// alongside it. Some header/plumbing logic is intentionally copied rather
// than shared for the same reason.

const DEFAULT_REQUIRED_COUNT = 5000;
const MAX_REQUIRED_COUNT = 100000;
const PREVIEW_DEBOUNCE_MS = 400;

const getErrorMessage = (error) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  error?.message ||
  'The request failed';

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(4) : '—';
};

const formatDate = (value) => {
  if (!value) return 'Not configured';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not configured' : date.toLocaleDateString();
};

const getGradeLabel = (assessment) => {
  const grades = [1, 2, 3, 4].filter(
    (grade) =>
      assessment?.[`grade_level${grade}`] === true ||
      assessment?.[`gradeLevel${grade}`] === true,
  );
  return grades.length ? grades.join(', ') : 'Not configured';
};

const emptyStatsState = () => ({
  current: null,
  preview: null,
  previewLoading: false,
  previewError: null,
  writing: false,
  writeError: null,
});

const emptyNormalizedScoreState = () => ({
  stale: false,
  preview: null,
  previewAttempted: false,
  previewLoading: false,
  previewError: null,
  running: false,
  runError: null,
});

const buildTaskRows = (context) => {
  const rows = {};
  (context?.tasks || []).forEach((task) => {
    rows[task.taskId] = {
      taskName: task.taskName,
      order: task.order,
      stats: {
        ...emptyStatsState(),
        current: task.taskStatistics || null,
      },
      normalizedScore: {
        ...emptyNormalizedScoreState(),
        stale: Boolean(task.normalizedScoreStale),
      },
    };
  });
  return rows;
};

function CalculateStandardisedScoresStepModal({ show, onHide }) {
  const [assessments, setAssessments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [screenerDatesByAssessmentId, setScreenerDatesByAssessmentId] = useState({});
  const [countryFilter, setCountryFilter] = useState('');
  const [assessmentId, setAssessmentId] = useState('');
  const [assessmentContext, setAssessmentContext] = useState(null);
  const [requiredAnswerCount, setRequiredAnswerCount] = useState(
    String(DEFAULT_REQUIRED_COUNT),
  );
  const [taskRows, setTaskRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [error, setError] = useState('');
  const [inFlightWrites, setInFlightWrites] = useState(0);
  const [statsBatchRunning, setStatsBatchRunning] = useState(false);
  const [normalizedScoreBatchRunning, setNormalizedScoreBatchRunning] = useState(false);
  const [confirmBatch, setConfirmBatch] = useState(null); // 'stats' | 'normalizedScore' | null
  const contextGeneration = useRef(0);
  const statsPreviewGeneration = useRef(0);

  useEffect(() => {
    setCountryFilter('');
    setAssessmentId('');
    setAssessmentContext(null);
    setRequiredAnswerCount(String(DEFAULT_REQUIRED_COUNT));
    setTaskRows({});
    setError('');
    setInFlightWrites(0);
    setStatsBatchRunning(false);
    setNormalizedScoreBatchRunning(false);
    setConfirmBatch(null);
    setScreenerDatesByAssessmentId({});

    if (!show) {
      setLoading(false);
      setContextLoading(false);
      return;
    }

    let active = true;
    const loadOptions = async () => {
      setLoading(true);
      setError('');
      try {
        const [assessmentOptions, countryOptions] = await Promise.all([
          getAssessmentTypes(),
          getCountries(),
        ]);
        if (!active) return;
        const eligibleAssessments = assessmentOptions.filter(
          (assessment) => assessment.task !== true && assessment.rti !== true,
        );
        setAssessments(eligibleAssessments);
        setCountries(countryOptions);

        const distinctCountryIds = [
          ...new Set(eligibleAssessments.map((a) => a.countryid).filter(Boolean)),
        ];
        const screenerLists = await Promise.all(
          distinctCountryIds.map((countryId) =>
            getCountryScreenersByCountryId(countryId).catch(() => []),
          ),
        );
        if (!active) return;
        const dateByAssessmentId = {};
        screenerLists.flat().forEach((screener) => {
          if (screener.assessmentid) {
            dateByAssessmentId[screener.assessmentid] = screener.dateopened || null;
          }
        });
        setScreenerDatesByAssessmentId(dateByAssessmentId);
      } catch (loadError) {
        if (active) setError(getErrorMessage(loadError));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadOptions();
    return () => {
      active = false;
    };
  }, [show]);

  const assessment = assessments.find((item) => item.id === assessmentId);
  const filteredAssessments = useMemo(() => {
    const base = countryFilter
      ? assessments.filter((item) => item.countryid === countryFilter)
      : assessments;
    return [...base].sort((a, b) => {
      const dateA = screenerDatesByAssessmentId[a.id];
      const dateB = screenerDatesByAssessmentId[b.id];
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return new Date(dateA) - new Date(dateB);
    });
  }, [assessments, countryFilter, screenerDatesByAssessmentId]);

  const loadContext = async (targetAssessment) => {
    const useAssessment = targetAssessment || assessment;
    if (!useAssessment) return;
    const generation = ++contextGeneration.current;
    setContextLoading(true);
    try {
      const context = await getAssessmentStatisticsContext(
        useAssessment.id,
        useAssessment.countryid,
      );
      if (contextGeneration.current !== generation) return;
      setAssessmentContext(context);
      setTaskRows(buildTaskRows(context));
      setError('');
    } catch (contextError) {
      if (contextGeneration.current === generation) {
        setError(getErrorMessage(contextError));
      }
    } finally {
      if (contextGeneration.current === generation) setContextLoading(false);
    }
  };

  const handleCountryChange = (event) => {
    setCountryFilter(event.target.value);
    setAssessmentId('');
    setAssessmentContext(null);
    setTaskRows({});
  };

  const handleAssessmentChange = (event) => {
    const nextId = event.target.value;
    setAssessmentId(nextId);
    setAssessmentContext(null);
    setTaskRows({});
    const nextAssessment = assessments.find((item) => item.id === nextId);
    if (nextAssessment) loadContext(nextAssessment);
  };

  const handleRequiredCountChange = (event) => {
    setRequiredAnswerCount(event.target.value);
  };

  const tasks = useMemo(() => {
    return (assessmentContext?.tasks || [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((task) => ({ id: task.taskId, name: task.taskName }));
  }, [assessmentContext]);

  const academicYear = assessmentContext?.academicYear || '';
  const selectedCount = Number(requiredAnswerCount);
  const countIsValid =
    requiredAnswerCount !== '' &&
    Number.isInteger(selectedCount) &&
    selectedCount > 0 &&
    selectedCount <= MAX_REQUIRED_COUNT;
  const countryName =
    countries.find((country) => country.id === assessmentContext?.countryId)?.name ||
    assessmentContext?.countryId ||
    'Not configured';

  // Auto-load every task's TaskStatistics preview whenever the scope or the
  // required count settles — debounced so typing a count doesn't fire a
  // request per keystroke. Read-only, so safe to run without user action.
  useEffect(() => {
    if (!assessmentContext || tasks.length === 0 || !countIsValid) return;
    const generation = ++statsPreviewGeneration.current;
    const timer = setTimeout(() => {
      tasks.forEach((task) => loadStatsPreview(task.id, generation));
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentContext, requiredAnswerCount, countIsValid]);

  const loadStatsPreview = async (taskId, generation) => {
    if (!assessmentContext) return;
    setTaskRows((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        stats: { ...prev[taskId].stats, previewLoading: true, previewError: null },
      },
    }));
    try {
      const preview = await calculateTaskStatsByNcount({
        taskId,
        ncount: selectedCount,
        assessmentId: assessment.id,
        countryId: assessmentContext.countryId,
      });
      if (statsPreviewGeneration.current !== generation) return;
      setTaskRows((prev) => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          stats: { ...prev[taskId].stats, preview, previewLoading: false, previewError: null },
        },
      }));
    } catch (previewError) {
      if (statsPreviewGeneration.current !== generation) return;
      setTaskRows((prev) => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          stats: {
            ...prev[taskId].stats,
            preview: null,
            previewLoading: false,
            previewError: getErrorMessage(previewError),
          },
        },
      }));
    }
  };

  const loadNormalizedScorePreview = async (taskId) => {
    setTaskRows((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        normalizedScore: {
          ...prev[taskId].normalizedScore,
          previewAttempted: true,
          previewLoading: true,
          previewError: null,
        },
      },
    }));
    try {
      const preview = await previewNormalizedScoreRecalculation({
        taskId,
        assessmentId: assessment.id,
        countryId: assessmentContext.countryId,
      });
      setTaskRows((prev) => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          normalizedScore: { ...prev[taskId].normalizedScore, preview: preview || null, previewLoading: false, previewError: null },
        },
      }));
    } catch (previewError) {
      setTaskRows((prev) => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          normalizedScore: {
            ...prev[taskId].normalizedScore,
            preview: null,
            previewLoading: false,
            previewError: getErrorMessage(previewError),
          },
        },
      }));
    }
  };

  // Auto-load Normalised Score previews once a task has current stats and is
  // flagged stale — triggers on context (re)load, not on every keystroke.
  // Guarded by previewAttempted (not just !preview) so a preview call that
  // legitimately resolves with no usable data can't retrigger itself forever.
  useEffect(() => {
    if (!assessmentContext) return;
    tasks.forEach((task) => {
      const row = taskRows[task.id];
      if (row?.stats.current && row.normalizedScore.stale && !row.normalizedScore.previewAttempted) {
        loadNormalizedScorePreview(task.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentContext, taskRows]);

  const publishOneTaskStatistics = async (taskId) => {
    setInFlightWrites((count) => count + 1);
    setTaskRows((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], stats: { ...prev[taskId].stats, writing: true, writeError: null } },
    }));
    try {
      const result = await publishTaskStatistics({
        taskId,
        ncount: selectedCount,
        assessmentId: assessment.id,
        countryId: assessmentContext.countryId,
      });
      setTaskRows((prev) => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          stats: {
            ...prev[taskId].stats,
            writing: false,
            writeError: null,
            current: {
              id: result.taskStatisticsId,
              ncount: result.ncount,
              mean: result.mean,
              standardDeviation: result.standardDeviation,
              windowStart: result.windowStart,
              windowEnd: result.windowEnd,
              publishedAt: result.publishedAt,
            },
          },
          normalizedScore: { ...emptyNormalizedScoreState(), stale: true },
        },
      }));
      return true;
    } catch (writeError) {
      setTaskRows((prev) => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          stats: { ...prev[taskId].stats, writing: false, writeError: getErrorMessage(writeError) },
        },
      }));
      return false;
    } finally {
      setInFlightWrites((count) => count - 1);
    }
  };

  const runOneNormalizedScoreRecalculation = async (taskId) => {
    setInFlightWrites((count) => count + 1);
    setTaskRows((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        normalizedScore: { ...prev[taskId].normalizedScore, running: true, runError: null },
      },
    }));
    try {
      await runNormalizedScoreRecalculation({
        taskId,
        assessmentId: assessment.id,
        countryId: assessmentContext.countryId,
      });
      setTaskRows((prev) => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          normalizedScore: { ...emptyNormalizedScoreState(), stale: false, running: false },
        },
      }));
      return true;
    } catch (runError) {
      setTaskRows((prev) => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          normalizedScore: { ...prev[taskId].normalizedScore, running: false, runError: getErrorMessage(runError) },
        },
      }));
      return false;
    } finally {
      setInFlightWrites((count) => count - 1);
    }
  };

  const statsReadyTaskIds = tasks
    .filter((task) => {
      const row = taskRows[task.id];
      return (
        row?.stats.preview &&
        !row.stats.previewError &&
        row.stats.preview.sampleCount === selectedCount &&
        row.stats.current?.ncount !== selectedCount
      );
    })
    .map((task) => task.id);

  const normalizedScoreReadyTaskIds = tasks
    .filter((task) => {
      const row = taskRows[task.id];
      return row?.stats.current && row.normalizedScore.stale && row.normalizedScore.preview;
    })
    .map((task) => task.id);

  const runStatsBatch = async () => {
    setConfirmBatch(null);
    setStatsBatchRunning(true);
    for (const taskId of statsReadyTaskIds) {
      // Sequential on purpose: one HTTP request per task, one at a time —
      // never Promise.all, never a server-side loop over tasks.
      // eslint-disable-next-line no-await-in-loop
      await publishOneTaskStatistics(taskId);
    }
    await loadContext();
    setStatsBatchRunning(false);
  };

  const runNormalizedScoreBatch = async () => {
    setConfirmBatch(null);
    setNormalizedScoreBatchRunning(true);
    for (const taskId of normalizedScoreReadyTaskIds) {
      // eslint-disable-next-line no-await-in-loop
      await runOneNormalizedScoreRecalculation(taskId);
    }
    await loadContext();
    setNormalizedScoreBatchRunning(false);
  };

  useEffect(() => {
    if (inFlightWrites === 0) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [inFlightWrites]);

  const handleHide = () => {
    if (inFlightWrites > 0) return;
    onHide();
  };

  const calculatedCount = tasks.filter((task) => taskRows[task.id]?.stats.current).length;
  const upToDateCount = tasks.filter(
    (task) => taskRows[task.id]?.stats.current && !taskRows[task.id]?.normalizedScore.stale,
  ).length;

  const busy = inFlightWrites > 0 || statsBatchRunning || normalizedScoreBatchRunning;

  return (
    <>
      <Modal show={show} onHide={busy ? undefined : handleHide} size="xl" centered>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Calculate Standardised Scores (Step-by-step)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            Calculates TaskStatistics and Normalised Scores one task at a time. Each step
            is its own saved action — please stay on this page until a calculation
            finishes.
          </Alert>

          {inFlightWrites > 0 && (
            <Alert variant="warning">
              A calculation is currently writing data — please stay on this page until it
              finishes. Closing now will not lose everything already calculated, but it
              will abandon whatever is running right now.
            </Alert>
          )}

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 mb-0">Loading assessments…</p>
            </div>
          ) : (
            <>
              <Row className="g-3 mb-4">
                <Col md={3}>
                  <Form.Group controlId="step-statistics-country-filter">
                    <Form.Label>Filter by country</Form.Label>
                    <Form.Select value={countryFilter} onChange={handleCountryChange} disabled={busy}>
                      <option value="">All countries</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>{country.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="step-statistics-assessment">
                    <Form.Label>Assessment</Form.Label>
                    <Form.Select value={assessmentId} onChange={handleAssessmentChange} disabled={busy}>
                      <option value="">Select an assessment…</option>
                      {filteredAssessments.map((option) => {
                        const screenerDate = screenerDatesByAssessmentId[option.id];
                        return (
                          <option key={option.id} value={option.id}>
                            {option.name}
                            {screenerDate ? ` — opens ${formatDate(screenerDate)}` : ''}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="step-statistics-required-answer-count">
                    <Form.Label>Required answer count</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max={MAX_REQUIRED_COUNT}
                      step="1"
                      value={requiredAnswerCount}
                      onChange={handleRequiredCountChange}
                      disabled={busy}
                      isInvalid={!countIsValid}
                    />
                    <Form.Control.Feedback type="invalid">
                      Enter a whole number from 1 to {MAX_REQUIRED_COUNT.toLocaleString()}.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {assessment && (
                <>
                  <Card className="mb-4">
                    <Card.Header>Country screener scope</Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        <Col md={3}><strong>Country</strong><div>{countryName}</div></Col>
                        <Col md={3}><strong>Grade(s)</strong><div>{getGradeLabel(assessmentContext)}</div></Col>
                        <Col md={3}><strong>Academic year</strong><div>{academicYear || 'Not available'}</div></Col>
                        <Col md={3}><strong>Statistics window</strong><div>{formatDate(assessmentContext?.dateOpened)} – {formatDate(assessmentContext?.dateClosed)}</div></Col>
                      </Row>
                      {tasks.length > 0 && (
                        <Row className="g-3 mt-1">
                          <Col>
                            <strong>Completeness</strong>
                            <div>
                              <Badge bg={calculatedCount === tasks.length ? 'success' : 'secondary'} className="me-2">
                                {calculatedCount} of {tasks.length} tasks calculated
                              </Badge>
                              <Badge bg={upToDateCount === tasks.length && tasks.length > 0 ? 'success' : 'secondary'}>
                                {upToDateCount} of {tasks.length} up to date
                              </Badge>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </Card.Body>
                  </Card>

                  {contextLoading ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading assessment scope…</div>
                  ) : assessmentContext?.blockedReason ? (
                    <Alert variant="warning">{assessmentContext.blockedReason}</Alert>
                  ) : null}

                  {!contextLoading && tasks.length === 0 ? (
                    <Alert variant="warning">No required tasks are configured for this assessment.</Alert>
                  ) : !contextLoading && (
                    <>
                      <div className="d-flex justify-content-end gap-2 mb-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={busy || statsReadyTaskIds.length === 0}
                          onClick={() => setConfirmBatch('stats')}
                        >
                          {statsBatchRunning ? <><Spinner animation="border" size="sm" className="me-2" />Calculating…</> : `Calculate All (${statsReadyTaskIds.length})`}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={busy || normalizedScoreReadyTaskIds.length === 0}
                          onClick={() => setConfirmBatch('normalizedScore')}
                        >
                          {normalizedScoreBatchRunning ? <><Spinner animation="border" size="sm" className="me-2" />Running…</> : `Run All (${normalizedScoreReadyTaskIds.length})`}
                        </Button>
                      </div>
                      <Table responsive bordered hover>
                        <thead>
                          <tr>
                            <th>Required task</th>
                            <th>TaskStatistics</th>
                            <th>Normalised Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tasks.map((task) => {
                            const row = taskRows[task.id];
                            if (!row) return null;
                            const stats = row.stats;
                            const normalizedScore = row.normalizedScore;
                            const isCurrentCount = stats.current?.ncount === selectedCount;

                            return (
                              <tr key={task.id}>
                                <td>{task.name}</td>
                                <td>
                                  {stats.current && (
                                    <div className="small text-muted mb-1">
                                      Published: {stats.current.ncount.toLocaleString()} answers,
                                      mean {formatNumber(stats.current.mean)}, sd {formatNumber(stats.current.standardDeviation)},
                                      {' '}{formatDate(stats.current.publishedAt)}
                                    </div>
                                  )}
                                  {isCurrentCount ? (
                                    <Badge bg="success">Published at {selectedCount.toLocaleString()}</Badge>
                                  ) : stats.writing ? (
                                    <Button size="sm" disabled>
                                      <Spinner animation="border" size="sm" className="me-2" />Calculating…
                                    </Button>
                                  ) : stats.previewLoading ? (
                                    <div><Spinner animation="border" size="sm" className="me-2" />Checking…</div>
                                  ) : stats.previewError ? (
                                    <div className="text-danger small">{stats.previewError}</div>
                                  ) : stats.preview ? (
                                    stats.preview.sampleCount === selectedCount ? (
                                      <div>
                                        <div className="small mb-1">
                                          {stats.preview.sampleCount.toLocaleString()} / {selectedCount.toLocaleString()} ready —
                                          {' '}mean {formatNumber(stats.preview.mean)}, sd {formatNumber(stats.preview.standardDeviation)}
                                        </div>
                                        <Button
                                          size="sm"
                                          variant="primary"
                                          disabled={statsBatchRunning || stats.writing}
                                          onClick={() => publishOneTaskStatistics(task.id)}
                                        >
                                          {stats.current ? 'Replace' : 'Calculate'}
                                        </Button>
                                      </div>
                                    ) : (
                                      <Badge bg="warning" text="dark">
                                        Below threshold ({stats.preview.sampleCount.toLocaleString()} / {selectedCount.toLocaleString()})
                                      </Badge>
                                    )
                                  ) : (
                                    <Badge bg="secondary">Not checked</Badge>
                                  )}
                                  {stats.writeError && (
                                    <div className="text-danger small mt-1">{stats.writeError}</div>
                                  )}
                                </td>
                                <td>
                                  {!stats.current ? (
                                    <span className="text-muted small">Calculate TaskStatistics first</span>
                                  ) : !normalizedScore.stale ? (
                                    <Badge bg="success">Up to date</Badge>
                                  ) : normalizedScore.running ? (
                                    <Button size="sm" disabled>
                                      <Spinner animation="border" size="sm" className="me-2" />Running…
                                    </Button>
                                  ) : normalizedScore.previewLoading ? (
                                    <div><Spinner animation="border" size="sm" className="me-2" />Checking…</div>
                                  ) : normalizedScore.previewError ? (
                                    <div className="text-danger small">{normalizedScore.previewError}</div>
                                  ) : normalizedScore.preview ? (
                                    <div>
                                      <div className="small mb-1">
                                        {normalizedScore.preview.toCreate.toLocaleString()} to create,{' '}
                                        {normalizedScore.preview.toUpdate.toLocaleString()} to update,{' '}
                                        {normalizedScore.preview.unchanged.toLocaleString()} unchanged
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="primary"
                                        disabled={normalizedScoreBatchRunning || normalizedScore.running}
                                        onClick={() => runOneNormalizedScoreRecalculation(task.id)}
                                      >
                                        Run Normalised Score for {task.name}
                                      </Button>
                                    </div>
                                  ) : (
                                    <Badge bg="secondary">Not checked</Badge>
                                  )}
                                  {normalizedScore.runError && (
                                    <div className="text-danger small mt-1">{normalizedScore.runError}</div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleHide} disabled={busy}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(confirmBatch)} onHide={() => setConfirmBatch(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmBatch === 'stats' ? 'Calculate all ready tasks' : 'Run all ready tasks'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmBatch === 'stats' ? (
            <>
              This will calculate TaskStatistics for {statsReadyTaskIds.length} ready task(s), one at
              a time. Please stay on this page until it finishes.
            </>
          ) : (
            <>
              This will run Normalised Score recalculation for {normalizedScoreReadyTaskIds.length} ready
              task(s), one at a time. Please stay on this page until it finishes.
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmBatch(null)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={confirmBatch === 'stats' ? runStatsBatch : runNormalizedScoreBatch}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CalculateStandardisedScoresStepModal;
